import React, { useState, useRef, useEffect } from 'react';
import { juices, categories, initialOrders } from './mock/data';
import Header from './components/Header';
import JuiceList from './components/JuiceList';
import CartSummary from './components/CartSummary';
import CustomJuiceCreator from './components/CustomJuiceCreator';
import Notification from './components/Notification';
import * as sb from './services/supabaseClient';
import AdminPanel from './components/admin/AdminPanel';
import AdminTools from './components/admin/AdminTools';
import CategoryManager from './components/admin/CategoryManager';
import OrdersPanel from './components/admin/OrdersPanel';

const App = () => {
  const [cartItems, setCartItems] = useState([]);
  // Decide si usar Supabase remoto (si está configurado) o localStorage
  const useRemote = sb.isSupabaseConfigured();
  // Feature flag: mostrar sección "Personaliza tu Jugo"
  const showCustomizer = false; // cambiar a true cuando quieras habilitarla

  // Persistencia en localStorage: cargar desde storage si existe, sino usar los mocks
  const [products, setProducts] = useState(() => {
    try {
      const raw = localStorage.getItem('products');
      return raw ? JSON.parse(raw) : juices;
    } catch (e) {
      console.warn('Error leyendo products desde localStorage', e);
      return juices;
    }
  });

  const [categoriesList, setCategoriesList] = useState(() => {
    try {
      const raw = localStorage.getItem('categories');
      return raw ? JSON.parse(raw) : categories;
    } catch (e) {
      console.warn('Error leyendo categories desde localStorage', e);
      return categories;
    }
  });

  const [customTypes, setCustomTypes] = useState(() => {
    try {
      const raw = localStorage.getItem('customTypes');
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      console.warn('Error leyendo customTypes desde localStorage', e);
      return [];
    }
  });

  const [ingredientsList, setIngredientsList] = useState(() => {
    try {
      const raw = localStorage.getItem('ingredients');
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      console.warn('Error leyendo ingredients desde localStorage', e);
      return [];
    }
  });

  const [orders, setOrders] = useState(() => {
    try {
      const raw = localStorage.getItem('orders');
      return raw ? JSON.parse(raw) : initialOrders;
    } catch (e) {
      console.warn('Error leyendo orders desde localStorage', e);
      return initialOrders;
    }
  });
  const [notification, setNotification] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const notificationTimeout = useRef(null);

  const showNotification = (message) => {
    setNotification(message);
    if (notificationTimeout.current) clearTimeout(notificationTimeout.current);
    notificationTimeout.current = setTimeout(() => {
      setNotification(null);
      notificationTimeout.current = null;
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (notificationTimeout.current) clearTimeout(notificationTimeout.current);
    };
  }, []);

  // Si está configurado Supabase, cargar datos remotos al iniciar
  useEffect(() => {
    if (!useRemote) return;

    let mounted = true;
    const load = async () => {
      try {
        const [remoteProducts, remoteCategories, remoteOrders] = await Promise.all([
          sb.fetchProducts(),
          sb.fetchCategories(),
          sb.fetchOrders()
        ]);
        if (!mounted) return;
        if (remoteProducts) setProducts(remoteProducts);
        if (remoteCategories) setCategoriesList(remoteCategories);
        if (remoteOrders) setOrders(remoteOrders);
        // fetch customization data if present
        try {
          const [remoteTypes, remoteIngredients] = await Promise.all([
            sb.fetchCustomTypes(),
            sb.fetchIngredients()
          ]);
          if (remoteTypes) setCustomTypes(remoteTypes);
          if (remoteIngredients) setIngredientsList(remoteIngredients);
        } catch (e) {
          console.warn('No se pudieron cargar tipos/ingredientes remotos', e);
        }
        showNotification('Datos cargados desde la base de datos');
      } catch (err) {
        console.error('Error cargando datos remotos', err);
        showNotification('Error al cargar datos remotos');
      }
    };

    load();
    return () => { mounted = false; };
  }, [useRemote]);

  // Detectar sesión de Supabase y actualizar isAdmin automáticamente
  useEffect(() => {
    if (!useRemote) return;
    let unsub = null;
    const init = async () => {
      try {
        const user = await sb.getUser();
        if (user) {
          // check admin membership
          try {
            const adm = await sb.isUserAdmin(user.id);
            setIsAdmin(Boolean(adm));
          } catch (e) {
            console.warn('Error comprobando admin', e);
          }
        }
      } catch (err) {
        console.warn('No se pudo obtener usuario', err);
      }
      unsub = sb.onAuthStateChange(async (event, session) => {
        if (event === 'SIGNED_IN') {
          try {
            const uid = session?.user?.id || null;
            const adm = uid ? await sb.isUserAdmin(uid) : false;
            setIsAdmin(Boolean(adm));
          } catch (e) {
            console.warn('Error comprobando admin on sign in', e);
            setIsAdmin(false);
          }
        }
        if (event === 'SIGNED_OUT') setIsAdmin(false);
      });
    };
    init();
    return () => {
      if (unsub) unsub();
    };
  }, [useRemote]);

  // Guardar cambios en localStorage para persistencia entre recargas
  useEffect(() => {
    try {
      localStorage.setItem('products', JSON.stringify(products));
    } catch (e) {
      console.warn('Error guardando products en localStorage', e);
    }
  }, [products]);

  useEffect(() => {
    try {
      localStorage.setItem('customTypes', JSON.stringify(customTypes));
    } catch (e) {
      console.warn('Error guardando customTypes en localStorage', e);
    }
  }, [customTypes]);

  useEffect(() => {
    try {
      localStorage.setItem('ingredients', JSON.stringify(ingredientsList));
    } catch (e) {
      console.warn('Error guardando ingredients en localStorage', e);
    }
  }, [ingredientsList]);

  useEffect(() => {
    try {
      localStorage.setItem('categories', JSON.stringify(categoriesList));
    } catch (e) {
      console.warn('Error guardando categories en localStorage', e);
    }
  }, [categoriesList]);

  useEffect(() => {
    try {
      localStorage.setItem('orders', JSON.stringify(orders));
    } catch (e) {
      console.warn('Error guardando orders en localStorage', e);
    }
  }, [orders]);

  const addToCart = (product) => {
    const safeProduct = {
      ...product,
      price: parseFloat(product.price) || 0
    };
    setCartItems(prevItems => [...prevItems, safeProduct]);
    showNotification(`¡${product.name} agregado al carrito!`);
  };

  const removeFromCart = (id) => {
    setCartItems(prevItems => {
      const index = prevItems.findIndex(item => item.id === id);
      if (index === -1) return prevItems;
      const newItems = [...prevItems];
      newItems.splice(index, 1);
      return newItems;
    });
    showNotification('Producto eliminado del carrito');
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      showNotification('¡Agrega productos al carrito primero!');
      return;
    }

    const total = cartItems.reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);
    const message = `Hola, quiero ordenar: ${cartItems.map(item => 
      `${item.name} - $${(parseFloat(item.price) || 0).toFixed(2)}`
    ).join('\n')}\nTotal: $${total.toFixed(2)}`;
    
    window.open(`https://wa.me/7223896761?text=${encodeURIComponent(message)}`, '_blank');
    setCartItems([]);
    showNotification('¡Pedido realizado con éxito!');
  };

  const addProduct = (newProduct) => {
    // Normalizar campos antes de enviar
    const product = {
      name: newProduct.name,
      description: newProduct.description || '',
      price: parseFloat(newProduct.price) || 0,
      category: newProduct.category || null,
      emoji: newProduct.emoji || ''
    };

    // Do not upload images for now; only save emoji. This avoids storage/RLS issues.
    if (useRemote) {
      sb.addProductRemote(product).then(remote => {
        setProducts(prev => [...prev, remote]);
        showNotification(`Producto ${remote.name} agregado`);
      }).catch(err => {
        console.error(err);
        showNotification('Error al agregar producto (remote)');
      });
    } else {
      const localProduct = { ...product, id: Date.now() };
      setProducts(prev => [...prev, localProduct]);
      showNotification(`Producto ${localProduct.name} agregado`);
    }
  };

  const updateProduct = (updatedProduct) => {
    if (useRemote) {
      sb.updateProductRemote(updatedProduct.id, updatedProduct).then(remote => {
        setProducts(prev => prev.map(p => p.id === remote.id ? remote : p));
        showNotification(`Producto ${remote.name} actualizado`);
      }).catch(err => {
        console.error(err);
        showNotification('Error al actualizar producto (remote)');
      });
    } else {
      setProducts(prev => prev.map(p => p.id === updatedProduct.id ? updatedProduct : p));
      showNotification(`Producto ${updatedProduct.name} actualizado`);
    }
  };

  const deleteProduct = (productId) => {
    if (useRemote) {
      sb.deleteProductRemote(productId).then(() => {
        setProducts(prev => prev.filter(p => p.id !== productId));
        showNotification('Producto eliminado');
      }).catch(err => {
        console.error(err);
        showNotification('Error al eliminar producto (remote)');
      });
    } else {
      setProducts(prev => prev.filter(p => p.id !== productId));
      showNotification('Producto eliminado');
    }
  };

  const addCategory = (newCategory) => {
    if (useRemote) {
      sb.addCategoryRemote(newCategory).then(remote => {
        setCategoriesList(prev => [...prev, remote]);
        showNotification(`Categoría ${remote.name} agregada`);
      }).catch(err => {
        console.error(err);
        showNotification('Error al agregar categoría (remote)');
      });
    } else {
      setCategoriesList(prev => [...prev, newCategory]);
      showNotification(`Categoría ${newCategory.name} agregada`);
    }
  };

  // Custom types & ingredients handlers
  const addCustomType = (type) => {
    if (useRemote) {
      sb.addCustomTypeRemote(type).then(remote => {
        setCustomTypes(prev => [...prev, remote]);
        showNotification(`Tipo ${remote.name} agregado`);
      }).catch(err => {
        console.error(err);
        showNotification('Error al agregar tipo (remote)');
      });
    } else {
      const local = { ...type, id: Date.now() };
      setCustomTypes(prev => [...prev, local]);
      showNotification(`Tipo ${local.name} agregado`);
    }
  };

  const updateCustomType = (id, updated) => {
    if (useRemote) {
      sb.updateCustomTypeRemote(id, updated).then(remote => {
        setCustomTypes(prev => prev.map(t => t.id === remote.id ? remote : t));
        showNotification(`Tipo ${remote.name} actualizado`);
      }).catch(err => {
        console.error(err);
        showNotification('Error al actualizar tipo (remote)');
      });
    } else {
      setCustomTypes(prev => prev.map(t => t.id === id ? updated : t));
      showNotification(`Tipo actualizado`);
    }
  };

  const deleteCustomType = (id) => {
    if (useRemote) {
      sb.deleteCustomTypeRemote(id).then(() => {
        setCustomTypes(prev => prev.filter(t => t.id !== id));
        showNotification('Tipo eliminado');
      }).catch(err => {
        console.error(err);
        showNotification('Error al eliminar tipo (remote)');
      });
    } else {
      setCustomTypes(prev => prev.filter(t => t.id !== id));
      showNotification('Tipo eliminado');
    }
  };

  const addIngredient = (ingredient) => {
    if (useRemote) {
      sb.addIngredientRemote(ingredient).then(remote => {
        setIngredientsList(prev => [...prev, remote]);
        showNotification(`Ingrediente ${remote.name} agregado`);
      }).catch(err => {
        console.error(err);
        showNotification('Error al agregar ingrediente (remote)');
      });
    } else {
      const local = { ...ingredient, id: Date.now() };
      setIngredientsList(prev => [...prev, local]);
      showNotification(`Ingrediente ${local.name} agregado`);
    }
  };

  const updateIngredient = (id, updated) => {
    if (useRemote) {
      sb.updateIngredientRemote(id, updated).then(remote => {
        setIngredientsList(prev => prev.map(i => i.id === remote.id ? remote : i));
        showNotification(`Ingrediente ${remote.name} actualizado`);
      }).catch(err => {
        console.error(err);
        showNotification('Error al actualizar ingrediente (remote)');
      });
    } else {
      setIngredientsList(prev => prev.map(i => i.id === id ? updated : i));
      showNotification('Ingrediente actualizado');
    }
  };

  const deleteIngredient = (id) => {
    if (useRemote) {
      sb.deleteIngredientRemote(id).then(() => {
        setIngredientsList(prev => prev.filter(i => i.id !== id));
        showNotification('Ingrediente eliminado');
      }).catch(err => {
        console.error(err);
        showNotification('Error al eliminar ingrediente (remote)');
      });
    } else {
      setIngredientsList(prev => prev.filter(i => i.id !== id));
      showNotification('Ingrediente eliminado');
    }
  };

  const updateCategory = (oldName, updatedCategory) => {
    if (useRemote) {
      sb.updateCategoryRemote(oldName, updatedCategory).then(remote => {
        setCategoriesList(prev => prev.map(c => c.name === oldName ? remote : c));
        showNotification(`Categoría actualizada a ${remote.name}`);
      }).catch(err => {
        console.error(err);
        showNotification('Error al actualizar categoría (remote)');
      });
    } else {
      setCategoriesList(prev => prev.map(c => c.name === oldName ? updatedCategory : c));
      showNotification(`Categoría actualizada a ${updatedCategory.name}`);
    }
  };

  const deleteCategory = (categoryName) => {
    if (products.some(p => p.category === categoryName)) {
      showNotification('No se puede eliminar: hay productos en esta categoría');
      return;
    }
    if (useRemote) {
      sb.deleteCategoryRemote(categoryName).then(() => {
        setCategoriesList(prev => prev.filter(c => c.name !== categoryName));
        showNotification(`Categoría ${categoryName} eliminada`);
      }).catch(err => {
        console.error(err);
        showNotification('Error al eliminar categoría (remote)');
      });
    } else {
      setCategoriesList(prev => prev.filter(c => c.name !== categoryName));
      showNotification(`Categoría ${categoryName} eliminada`);
    }
  };

  const updateOrderStatus = (orderId, newStatus) => {
    if (useRemote) {
      sb.updateOrderStatusRemote(orderId, newStatus).then(remote => {
        setOrders(prev => prev.map(o => o.id === remote.id ? remote : o));
        showNotification(`Pedido #${orderId} actualizado a ${newStatus}`);
      }).catch(err => {
        console.error(err);
        showNotification('Error al actualizar pedido (remote)');
      });
    } else {
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
      showNotification(`Pedido #${orderId} actualizado a ${newStatus}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header 
        isAdmin={isAdmin} 
        showCustomizer={showCustomizer}
        onAdminAccess={(user) => {
          // user can be used for finer-grained checks later
          setIsAdmin(true);
          showNotification('Sesión iniciada');
        }}
        onLogoutAdmin={() => {
          // cerrar sesión en Supabase si corresponde
          if (sb.isSupabaseConfigured()) {
            sb.signOut().catch(err => console.warn('Error signOut', err));
          }
          setIsAdmin(false);
          showNotification('Sesión cerrada');
        }} 
      />
      
      {/* Eliminado el div con el botón admin */}
      {/* ... */}

      {notification && (
        <Notification message={notification} onClose={() => setNotification(null)} />
      )}

      {isAdmin ? (
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <AdminPanel
                products={products}
                categories={categoriesList}
                onAddProduct={addProduct}
                onUpdateProduct={updateProduct}
                onDeleteProduct={deleteProduct}
                customTypes={customTypes}
                ingredients={ingredientsList}
                onAddType={addCustomType}
                onUpdateType={updateCustomType}
                onDeleteType={deleteCustomType}
                onAddIngredient={addIngredient}
                onUpdateIngredient={updateIngredient}
                onDeleteIngredient={deleteIngredient}
              />
              <OrdersPanel
                orders={orders}
                onUpdateOrderStatus={updateOrderStatus}
              />
            </div>
            <div>
                {/* Herramientas administrativas (crear admins) colocadas en el dashboard principal */}
                <AdminTools isAdmin={isAdmin} onShowNotification={showNotification} />
              
              <CategoryManager
                categories={categoriesList}
                onAddCategory={addCategory}
                onUpdateCategory={updateCategory}
                onDeleteCategory={deleteCategory}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="container mx-auto px-4 py-8">
          {showCustomizer && (
            <section id="personaliza" className="mb-16">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Personaliza tu Jugo</h2>
              <CustomJuiceCreator 
                onAddToCart={addToCart} 
                types={customTypes}
                ingredients={ingredientsList}
                categories={categoriesList} 
              />
            </section>
          )}

          <section id="menu" className="mb-16">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Nuestro Menú</h2>
            <JuiceList juices={products} onAddToCart={addToCart} />
          </section>

          <section id="pedidos">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Tu Pedido</h2>
            <CartSummary 
              items={cartItems} 
              onCheckout={handleCheckout} 
              onRemove={removeFromCart}
            />
          </section>
        </div>
      )}
    </div>
  );
};

export default App;

// DONE